/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { useState, useEffect, Fragment } from "react";
import PropTypes from "prop-types";
import { Divider } from "@react-spectrum/divider";
import { ActionButton } from "@react-spectrum/button";
import { Flyout } from "components/quickTools/common/Flyout";
import FlyoutCss from "components/quickTools/common/Flyout.css";
import FlyoutTabItem from "components/quickTools/common/FlyoutTabItem";
import classNames from "classnames";
import { Switch } from "@react-spectrum/switch";
import DarkPageThemeIcon from "dc-icons/chrome_extension/SDC_SunMoon_18_N_Native.svg";
import PrimaryPanelCss from "components/quickTools/Primary/PrimaryPanel.css";
import { TooltipTrigger, Tooltip } from "@react-spectrum/tooltip";
import OverlayTrigger from "@react/react-spectrum/OverlayTrigger";
import Popover from "@react/react-spectrum/Popover";
import { triggerDocumentViewThemeChange } from "publicApis/Events/events";
import { TOOLTIP_DELAY } from "constants/constants";
import { BUTTON_STYLES } from "dc-preview-utils/src/Constants/constants";
import { trackNightModeFlyout, trackNightModePageThemeSwitch, trackNightModeAppThemeChange } from "analytics/events";

const PageThemeFlyout = props => {
  const {
    intl,
    handlePanelToggle,
    showFlyover,
    isDarkPageThemeEnabled,
    docViewTheme,
    isDarkPageThemeSwitchDisabled,
    setDocViewTheme,
    changeAppTheme,
    setDarkPageTheme,
    userPrefThemeCallback,
    customReadingThemesSet
  } = props;

  const ReadingThemes = customReadingThemesSet?.length
    ? customReadingThemesSet
    : [
        {
          label: intl.formatMessage({ id: "nightmode.flyout.label.system" }),
          verbId: "auto",
          isSelected: true
        },
        {
          label: intl.formatMessage({ id: "nightmode.flyout.label.light" }),
          verbId: "light",
          isSelected: false
        },
        {
          label: intl.formatMessage({ id: "nightmode.flyout.label.dark" }),
          verbId: "dark",
          isSelected: false
        }
      ];

  const [documentViewTheme, setDocumentViewTheme] = useState(ReadingThemes);
  const [initialAutoHide, setInitialAutoHide] = useState(false);

  const switchPageTheme = darkPageTheme => {
    setDarkPageTheme(darkPageTheme);
    const payload = {
      docViewTheme,
      isDarkPageThemeEnabled: darkPageTheme
    };
    triggerDocumentViewThemeChange(payload);
    trackNightModePageThemeSwitch(darkPageTheme);
  };

  useEffect(() => {
    setViewTheme(docViewTheme);
  }, [docViewTheme]);

  const setViewTheme = theme => {
    const updatedThemes = documentViewTheme.map(verb => ({
      ...verb,
      isSelected: verb.verbId === theme
    }));
    setDocumentViewTheme(updatedThemes);
  };

  const handleThemeChange = (theme, darkPageTheme) => {
    setDocViewTheme(theme);
    const resolvedTheme =
      theme === "auto" ? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light") : theme;
    changeAppTheme(resolvedTheme);

    // trigger event
    const payload = {
      docViewTheme: theme,
      isDarkPageThemeEnabled: darkPageTheme
    };
    triggerDocumentViewThemeChange(payload);
    // close flyout
    handlePanelToggle("themeSwitcher", false);
    trackNightModeAppThemeChange(theme);
  };

  const handleTabItemClick = verbId => {
    setViewTheme(verbId);

    const selectedTheme = documentViewTheme.find(verb => verb.verbId === verbId);
    handleThemeChange(selectedTheme.verbId, isDarkPageThemeEnabled);
  };

  const getThemeGroup = () => (
    <Fragment>
      {documentViewTheme.map(verb => (
        <FlyoutTabItem
          key={verb.verbId}
          verbClickHandler={() => handleTabItemClick(verb.verbId)}
          label={verb.label}
          data-test-id={verb.label}
          isSelected={verb.isSelected}
        />
      ))}
      <Divider size="M" UNSAFE_className={PrimaryPanelCss.divideRule} />
      <Switch
        isEmphasized
        UNSAFE_className={classNames(PrimaryPanelCss.CoverModeSwitch, PrimaryPanelCss.ThemeModeSwitch)}
        isSelected={isDarkPageThemeEnabled}
        value={isDarkPageThemeEnabled}
        onChange={() => switchPageTheme(!isDarkPageThemeEnabled)}
        aria-label={intl.formatMessage({ id: "nightmode.flyout.label.darkPageTheme" })}
        label={intl.formatMessage({ id: "nightmode.flyout.label.darkPageTheme" })}
        isDisabled={isDarkPageThemeSwitchDisabled}
        data-test-id="night-mode-dark-page-theme"
      >
        {intl.formatMessage({ id: "nightmode.flyout.label.darkPageTheme" })}
      </Switch>
    </Fragment>
  );

  const PopoverComponent = () => (
    <div role="tablist" className={FlyoutCss.FlyoutTabList}>
      {getThemeGroup()}
    </div>
  );

  const keyDownHandler = () => {
    setInitialAutoHide(false);
  };

  const handleFlyoutBtnClick = showFlyover => {
    handlePanelToggle("themeSwitcher", !showFlyover);
    if (userPrefThemeCallback) {
      userPrefThemeCallback()
        .then(theme => {
          setViewTheme(theme);
        })
        .catch(error => {
          console.log(error);
        });
    }
    trackNightModeFlyout();
  };

  return (
    <div className={PrimaryPanelCss.ThemeSwitcherWrapper} data-testid="page-theme-flyout" onKeyDown={keyDownHandler}>
      <OverlayTrigger trigger="click" show={showFlyover}>
        <TooltipTrigger trigger="hover" placement="left" delay={TOOLTIP_DELAY}>
          <ActionButton
            isQuiet
            holdAffordance
            id="theme-switcher"
            data-test-id="page-theme-flyout-btn"
            aria-label={intl.formatMessage({ id: "nightmode.flyout.tooltip" })}
            onPress={() => handleFlyoutBtnClick(showFlyover)}
            UNSAFE_style={showFlyover && BUTTON_STYLES.SELECTED}
          >
            <DarkPageThemeIcon />
          </ActionButton>
          <Tooltip data-test-id="flyout-tooltip">{intl.formatMessage({ id: "nightmode.flyout.tooltip" })}</Tooltip>
        </TooltipTrigger>
        <Popover isDialog={false}>
          <Flyout
            closeFlyout={() => {
              const flyoutTrigger = document.getElementById("theme-switcher");
              if (flyoutTrigger) {
                flyoutTrigger.focus();
              }
              handlePanelToggle("themeSwitcher", false);
              setInitialAutoHide(false);
            }}
            component={PopoverComponent}
            initialAutoHide={initialAutoHide}
          />
        </Popover>
      </OverlayTrigger>
    </div>
  );
};

export { PageThemeFlyout };
